import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../contexts/AuthContext';
import { usePrivacy } from '../../../contexts/PrivacyContext';
import { FiSend } from "react-icons/fi";
import { MdCheck } from "react-icons/md";
import { fetchAdminsByCourseId, getCourseSettings, getCourseEmailTemplate, listCourseEmailTemplates } from '../../../admin';
import { getCourseId } from '../../../firebase';
import { FiChevronDown, FiX } from "react-icons/fi";

const AvatarStack = React.memo(({ members, groupMemberSet, adminEmailSet }) => {
    const { maskInitial } = usePrivacy();
    if (!members || members.length === 0) return null;

    return (
        <StackContainer>
            {members.map((m, index) => {
                const isGroupMember = groupMemberSet.has(m.email);
                const isAdmin = adminEmailSet.has(m.email);
                const textColor = isAdmin && !isGroupMember ? "white" : "black";

                return (
                    <AvatarCircle
                        key={m.email}
                        bg={m.color}
                        textColor={textColor}
                        style={{
                            zIndex: members.length - index,
                            marginLeft: index === 0 ? 0 : -8
                        }}
                    >
                        {maskInitial(m.name) || m.initial}
                    </AvatarCircle>
                );
            })}
        </StackContainer>
    );
});

const EmailPopup = ({ isOpen, onClose, groupMembers }) => {
    const { groupInfo } = useAuth();
    const { maskName, maskEmail, maskInitial } = usePrivacy();
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [ccRecipients, setCcRecipients] = useState([]);
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [error, setError] = useState('');
    const [adminEmails, setAdminEmails] = useState([]);
    const [showAdminSearch, setShowAdminSearch] = useState(false);
    const [courseTemplates, setCourseTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [courseId, setCourseId] = useState(null);


    const defaultSubject = useMemo(
        () => `TeamCAST Update from ${groupInfo?.name || ''}`,
        [groupInfo?.name]
    );

    const adminEmailSet = useMemo(
        () => new Set(adminEmails),
        [adminEmails]
    );

    const groupMemberSet = useMemo(
        () => new Set(groupMembers?.map(m => m.email)),
        [groupMembers]
    );

    const combinedMembers = useMemo(
        () => [...selectedRecipients, ...ccRecipients],
        [selectedRecipients, ccRecipients]
    );

    const ccSet = useMemo(
        () => new Set(ccRecipients.map(r => r.email)),
        [ccRecipients]
    );

    const selectedAdminEmails = useMemo(
        () => ccRecipients
            .filter(r => adminEmailSet.has(r.email))
            .map(r => r.email),
        [ccRecipients, adminEmailSet]
    );

    const availableAdminEmails = useMemo(
        () => adminEmails.filter(email => !ccSet.has(email)),
        [adminEmails, ccSet]
    );

    // Fetch admin emails when popup opens
    useEffect(() => {
        const fetchAdmins = async () => {
            console.log(groupInfo);
            if (isOpen && groupInfo?.groupId) {
                try {
                    const courseId = await getCourseId(groupInfo.groupId);
                    if (courseId) {
                        const admins = await fetchAdminsByCourseId(courseId);
                        setAdminEmails(admins || []);
                    }
                } catch (error) {
                    console.error('Error fetching admins:', error);
                }
            }
        };
        fetchAdmins();
    }, [isOpen, groupInfo]);

    // Initialize recipients and load course templates/default template when popup opens
    useEffect(() => {
        if (!isOpen) return;

        if (groupMembers?.length > 0) {
            setSelectedRecipients([...groupMembers]);
        }

        setCcRecipients([]);
        setError('');
        setShowAdminSearch(false);
        setCourseTemplates([]);
        setSelectedTemplateId("");
        setCourseId(null);

        let cancelled = false;

        const loadCourseContext = async () => {
            if (!groupInfo?.groupId) {
                if (!cancelled) {
                    setSubject(defaultSubject);
                    setContent('');
                }
                return;
            }

            try {
                setLoadingTemplates(true);

                const cid = await getCourseId(groupInfo.groupId);
                if (cancelled || !cid) return;

                setCourseId(cid);

                const [settings, templates] = await Promise.all([
                    getCourseSettings(cid),
                    listCourseEmailTemplates(cid),
                ]);

                if (cancelled) return;

                const templateList = Array.isArray(templates) ? templates : [];
                setCourseTemplates(templateList);

                const defaultTemplateId = settings?.defaultTemplateId ?? "";
                setSelectedTemplateId(defaultTemplateId);

                const defaultTemplate = templateList.find(t => t.id === defaultTemplateId);

                setSubject(defaultTemplate?.subject ?? defaultSubject);
                setContent(defaultTemplate?.body ?? '');

            } catch (err) {
                console.error(err);
                if (!cancelled) {
                    setSubject(defaultSubject);
                    setContent('');
                }
            } finally {
                if (!cancelled) setLoadingTemplates(false);
            }
        };

        loadCourseContext();

        return () => { cancelled = true };

    }, [isOpen, groupInfo?.groupId, defaultSubject]);

    // Load selected template into subject/body
    useEffect(() => {
        if (!selectedTemplateId) {
            setSubject(defaultSubject);
            setContent('');
            return;
        }

        const selected = courseTemplates.find(t => t.id === selectedTemplateId);

        if (selected) {
            setSubject(selected.subject ?? defaultSubject);
            setContent(selected.body ?? '');
        }

    }, [selectedTemplateId, courseTemplates, defaultSubject]);

    const handleToggleRecipient = useCallback((member) => {
        setSelectedRecipients(prev =>
            prev.some(m => m.email === member.email)
                ? prev.filter(m => m.email !== member.email)
                : [...prev, member]
        );
    }, []);

    const handleSelectAll = () => {
        if (selectedRecipients.length === groupMembers.length) {
            setSelectedRecipients([]);
        } else {
            setSelectedRecipients([...groupMembers]);
        }
    };

    const handleAddAdminEmail = (adminEmail) => {
        if (!adminEmail || adminEmail === '') return;

        // Check if admin email is already in CC recipients
        if (!ccRecipients.some(r => r.email === adminEmail)) {
            // Create a simple member object for admin
            const adminMember = {
                email: adminEmail,
                name: adminEmail.split('@')[0], // Use email prefix as name
                initial: adminEmail.charAt(0).toUpperCase(),
                color: '#9333ea' // Purple color for admins
            };
            setCcRecipients(prev => [...prev, adminMember]);
        }
    };

    const handleRemoveAdminEmail = (adminEmail) => {
        setCcRecipients(prev => prev.filter(r => r.email !== adminEmail));
    };


    const handleSendEmail = useCallback(() => {
        if (!selectedRecipients.length) return setError('Please select at least one recipient');
        if (!subject.trim()) return setError('Please enter a subject');
        if (!content.trim()) return setError('Please enter email content');

        const recipientEmails = selectedRecipients.map(r => r.email).join(',');
        const ccEmails = ccRecipients.map(r => r.email).join(',');

        let mailto = `mailto:${recipientEmails}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(content)}`;
        if (ccEmails) mailto += `&cc=${ccEmails}`;

        window.location.href = mailto;
        setTimeout(onClose, 500);

    }, [selectedRecipients, ccRecipients, subject, content, onClose]);


    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <PopupContainer onClick={(e) => e.stopPropagation()}>
                <Header>
                    <Title>Send Email to Group</Title>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </Header>

                <Content>
                    <ScrollArea>
                        <Section>
                            <SectionTitle>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    Recipients ({selectedRecipients.length}{ccRecipients.length > 0 ? `, CC: ${ccRecipients.length}` : ''})
                                    <AvatarStack members={combinedMembers} groupMemberSet={groupMemberSet} adminEmailSet={adminEmailSet} />
                                </div>
                                {groupMembers && groupMembers.length > 0 && (
                                    <SelectAllButton onClick={handleSelectAll}>
                                        {selectedRecipients.length === groupMembers.length ? 'Deselect All' : 'Select All'}
                                    </SelectAllButton>
                                )}
                            </SectionTitle>
                            <RecipientsList>
                                {groupMembers && groupMembers.length > 0 ? (
                                    groupMembers.map((member) => {
                                        const isSelected = selectedRecipients.some(m => m.email === member.email);
                                        return (
                                            <RecipientItem key={member.email}>
                                                <CheckboxWrapper>
                                                    <HiddenCheckbox
                                                        checked={isSelected}
                                                        onChange={() => handleToggleRecipient(member)}
                                                    />
                                                    <StyledCheckbox checked={isSelected}>
                                                        {isSelected && <CheckIcon />}
                                                    </StyledCheckbox>
                                                </CheckboxWrapper>
                                                <Avatar bg={member.color} color={"#111827"}>
                                                    {maskInitial(member.name) || member.initial}
                                                </Avatar>

                                                <RecipientInfo>
                                                    <RecipientName>{maskName(member.name)}</RecipientName>
                                                    <RecipientEmail>{maskEmail(member.email)}</RecipientEmail>
                                                </RecipientInfo>
                                            </RecipientItem>
                                        );
                                    })
                                ) : (
                                    <NoRecipients>No group members available</NoRecipients>
                                )}
                            </RecipientsList>
                        </Section>

                        {/* Optional Admin Email Select */}
                        <Section>
                            <SectionTitle>
                                <span>Add Admin to CC (Optional)</span>
                                <ToggleButton onClick={() => setShowAdminSearch(!showAdminSearch)}>
                                    {showAdminSearch ? 'Hide' : 'Show'}
                                </ToggleButton>
                            </SectionTitle>

                            {showAdminSearch && (
                                <AdminSelectContainer>
                                    <AdminSelectWrapper>
                                        {/* Pills for selected admins */}
                                        {selectedAdminEmails.map((adminEmail) => {
                                            const adminMember = ccRecipients.find(r => r.email === adminEmail);
                                            return (
                                                <SelectedAdminItem key={adminEmail}>
                                                    <AdminAvatar bg={adminMember?.color || '#9333ea'}>
                                                        {adminMember?.initial || adminEmail.charAt(0).toUpperCase()}
                                                    </AdminAvatar>
                                                    <AdminEmailDisplay>{adminEmail}</AdminEmailDisplay>
                                                    <RemoveButton onClick={() => handleRemoveAdminEmail(adminEmail)}>
                                                        <FiX size={12} color="white" />   
                                                    </RemoveButton>
                                                </SelectedAdminItem>
                                            );
                                        })}

                                        {/* Dropdown to select new admin */}
                                        {availableAdminEmails.length > 0 && (
                                            <AdminSelect
                                                value=""
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        handleAddAdminEmail(e.target.value);
                                                        e.target.value = ''; // reset dropdown
                                                    }
                                                }}
                                            >
                                                <option value="">Select an admin...</option>
                                                {availableAdminEmails.map(email => (
                                                    <option key={email} value={email}>
                                                        {email}
                                                    </option>
                                                ))}
                                            </AdminSelect>
                                        )}
                                    </AdminSelectWrapper>
                                </AdminSelectContainer>
                            )}
                        </Section>


                        <Section>
                            <SectionTitle>Template</SectionTitle>
                            <SelectWrapper>
                                <StyledSelect
                                    value={selectedTemplateId}
                                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                                    disabled={loadingTemplates || !courseId || courseTemplates.length === 0}
                                >
                                    <option value="">
                                        {loadingTemplates
                                            ? "Loading templates..."
                                            : courseTemplates.length === 0
                                                ? "No templates available"
                                                : "Select a template"}
                                    </option>
                                    {courseTemplates.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.title || t.subject || "Untitled template"}
                                        </option>
                                    ))}
                                </StyledSelect>
                                <SelectIcon>
                                    <FiChevronDown size={18} />
                                </SelectIcon>
                            </SelectWrapper>
                        </Section>

                        <Section>
                            <SectionTitle>Subject</SectionTitle>
                            <Input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Enter email subject"
                            />
                        </Section>

                        <Section>
                            <SectionTitle>Content</SectionTitle>
                            <TextArea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="Enter email content"
                                rows={10}
                            />
                        </Section>
                    </ScrollArea>
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </Content>


                <Footer>
                    <CancelButton onClick={onClose}>Cancel</CancelButton>

                    <SendButton onClick={handleSendEmail}>
                        <span>Send Email</span>
                        <FiSend size={16} />
                    </SendButton>
                </Footer>
            </PopupContainer>
        </Overlay>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const PopupContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #111827;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 28px;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    
    &:hover {
        background-color: #f3f4f6;
        color: #111827;
    }
`;

const Content = styled.div`
  padding: 12px 24px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  min-height: 0;
    /* scrollbar polish */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 8px;
  }
`;


const Section = styled.div`
    margin-bottom: 24px;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SelectAllButton = styled.button`
    background: none;
    border: none;
    color: #9333ea;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    
    &:hover {
        background-color: #f3f4f6;
    }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  background-color: ${({ bg }) => bg};
  color: ${({ color }) => color};
`;

const RecipientsList = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
  max-height: 192px;
  overflow-y: auto;
`;

const RecipientItem = styled.label`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;

  &:hover {
    background-color: white;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const RecipientInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 12px;
`;

const RecipientName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const RecipientEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const NoRecipients = styled.div`
    padding: 20px;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2) inset;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const StyledSelect = styled.select`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 0 14px;
  background: #f8fafc;
  color: #111827;
  font-size: 14px;
  appearance: none;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2) inset;
  }
`;

const SelectIcon = styled.div`
    position: absolute;
    right: 12px;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    color: #9ca3af;
    pointer-events: none;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  resize: none;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2) inset;
  }
`;

const ErrorMessage = styled.div`
    background-color: #fef2f2;
    color: #dc2626;
    padding: 12px;
    border-radius: 6px;
    font-size: 14px;
`;

const Footer = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #fafafa;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  background: transparent;
  cursor: pointer;
  border: none;

  &:hover {
    background: #f3f4f6;
  }
`;

const SendButton = styled.button`
  background: #8b5cf6;
  color: white;
  padding: 10px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  border: none;

  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(139, 92, 246, 0.2);

  &:hover {
    opacity: 0.9;
  }
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  margin-right: 8px;
  cursor: pointer;
  position: relative;
`;

const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
`;

const StyledCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 20px; // slightly rounded corners
  border: 2px solid #8b5cf6;
  background-color: ${({ checked }) => (checked ? "#8b5cf6" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.2s;

  ${CheckboxWrapper}:hover & {
    border-color: #9333ea; // hover border color
  }

  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
  }
`;

const CheckIcon = styled(MdCheck)`
  font-weight: 700;
  width: 16px;
  height: 16px;
`;

const StackContainer = styled.div`
  display: flex;
  align-items: center;
`;

const AvatarCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  border: 1px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  color: ${({ textColor }) => textColor || "black"};
  background-color: ${({ bg }) => bg};
  box-shadow: 0 0 0 1px rgba(0,0,0,0.05);
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #9333ea;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  
  &:hover {
    background-color: #f3f4f6;
  }
`;

const AdminSelectContainer = styled.div`
  position: relative;
`;



const AdminSelectWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  min-height: 46px;
  background: white;
`;

const AdminSelect = styled.select`
  flex: 1;
  min-width: 150px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  padding: 6px 12px;
  cursor: pointer;
  appearance: none;

  &::-ms-expand {
    display: none;
  }
`;

const SelectedAdminItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #f3f0ff;
  border: 1px solid #e5d7ff;
  border-radius: 12px;
  font-size: 12px;
`;

const AdminAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${({ bg }) => bg};
  color: white;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AdminEmailDisplay = styled.div`
  font-size: 12px;
  color: #111827;
`;

const RemoveButton = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  border: none;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #dc2626;
  }
`;

export default EmailPopup;

